import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import {
  getRequestsByEmployee,
  getPendingRequests,
  getAllRequests,
  createRequest,
  approveRequest,
  rejectRequest,
  cancelRequest,
  getAllEmployees
} from '../../../services/timeOffService'
import RequestModal from '../components/RequestModal'
import RequestCard from '../components/RequestCard'
import RejectModal from '../components/RejectModal'

const TimeOff = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Requests State
  const [myRequests, setMyRequests] = useState([])
  const [pendingRequests, setPendingRequests] = useState([])
  const [allRequests, setAllRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Filter State (Admin)
  const [statusFilter, setStatusFilter] = useState('all')
  const [employeeFilter, setEmployeeFilter] = useState('all')
  const [employees, setEmployees] = useState([])

  // Modal State
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [rejectingRequest, setRejectingRequest] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Load user data and requests
  useEffect(() => {
    loadUserAndRequests()
  }, [])

  const loadUserAndRequests = async () => {
    try {
      setLoading(true)
      setError('')

      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      setUser(currentUser)

      // Get user data from Firestore
      const userDataResult = await getUserData(currentUser.uid)

      if (!userDataResult.success) {
        setError('Failed to load user data')
        setLoading(false)
        return
      }

      setUserData(userDataResult.data)

      // Check if user is admin
      const adminStatus =
        userDataResult.data.role === 'super_admin' ||
        userDataResult.data.role === 'admin'
      setIsAdmin(adminStatus)

      // Load requests based on role
      if (adminStatus) {
        await loadAdminData(userDataResult.data)
      } else {
        await loadEmployeeData(userDataResult.data.employeeId)
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading data')
    } finally {
      setLoading(false)
    }
  }

  const loadEmployeeData = async (employeeId) => {
    const result = await getRequestsByEmployee(employeeId)
    if (result.success) {
      setMyRequests(result.data)
    } else {
      setError(result.error || 'Failed to load requests')
    }
  }

  const loadAdminData = async (userData) => {
    // Load pending requests
    const pendingResult = await getPendingRequests(userData)
    if (pendingResult.success) {
      setPendingRequests(pendingResult.data)
    }

    // Load all requests
    const allResult = await getAllRequests(userData)
    if (allResult.success) {
      setAllRequests(allResult.data)
    }

    // Load employees for filter
    const employeesResult = await getAllEmployees()
    if (employeesResult.success) {
      setEmployees(employeesResult.data)
    }

    // Still load own requests for admin
    const myResult = await getRequestsByEmployee(userData.employeeId)
    if (myResult.success) {
      setMyRequests(myResult.data)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleOpenRequestModal = () => {
    setIsRequestModalOpen(true)
  }

  const handleCloseRequestModal = () => {
    setIsRequestModalOpen(false)
  }

  const handleSubmitRequest = async (formData) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      const result = await createRequest(formData, {
        employeeId: userData.employeeId,
        uid: user.uid,
        fullName: userData.fullName,
        email: userData.email
      })

      if (result.success) {
        // Reload data
        if (isAdmin) {
          await loadAdminData(userData)
        } else {
          await loadEmployeeData(userData.employeeId)
        }

        setSuccessMessage('Time off request submitted successfully!')
        handleCloseRequestModal()

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to submit request')
      }
    } catch (error) {
      console.error('Submit error:', error)
      setError('An error occurred while submitting the request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleApprove = async (requestId) => {
    setError('')
    setSuccessMessage('')

    try {

      const result = await approveRequest(requestId, {
        uid: user.uid,
        employeeId: userData.employeeId,
        role: userData.role,
        fullName: userData.fullName,
        email: userData.email
      })

      if (result.success) {
        // Reload admin data
        await loadAdminData(userData)
        setSuccessMessage('Request approved successfully!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to approve request')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Approve error:', error)
      setError('An error occurred while approving the request')
      setTimeout(() => setError(''), 5000)
    }
  }

  const handleOpenRejectModal = (request) => {
    setRejectingRequest(request)
    setIsRejectModalOpen(true)
  }

  const handleCloseRejectModal = () => {
    setIsRejectModalOpen(false)
    setRejectingRequest(null)
  }

  const handleSubmitReject = async (comments) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      const result = await rejectRequest(rejectingRequest.id, {
        uid: user.uid,
        employeeId: userData.employeeId,
        role: userData.role,
        fullName: userData.fullName,
        email: userData.email
      }, comments)

      if (result.success) {
        // Reload admin data
        await loadAdminData(userData)
        setSuccessMessage('Request rejected successfully!')
        handleCloseRejectModal()
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to reject request')
      }
    } catch (error) {
      console.error('Reject error:', error)
      setError('An error occurred while rejecting the request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async (requestId) => {
    setError('')
    setSuccessMessage('')

    try {
      const result = await cancelRequest(requestId)

      if (result.success) {
        // Reload data
        if (isAdmin) {
          await loadAdminData(userData)
        } else {
          await loadEmployeeData(userData.employeeId)
        }

        setSuccessMessage('Request cancelled successfully!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to cancel request')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Cancel error:', error)
      setError('An error occurred while cancelling the request')
      setTimeout(() => setError(''), 5000)
    }
  }

  // Filter all requests for admin view
  const getFilteredRequests = () => {
    let filtered = [...allRequests]

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter)
    }

    // Filter by employee
    if (employeeFilter !== 'all') {
      filtered = filtered.filter(req => req.employeeId === employeeFilter)
    }

    return filtered
  }

  const filteredRequests = isAdmin ? getFilteredRequests() : []

  // Loading State
  if (loading) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Time Off Requests"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-primary)',
              marginBottom: '1rem'
            }}
          ></i>
          <p style={{ color: 'var(--psyber-text-secondary)' }}>Loading requests...</p>
        </Card>
      </div>
    )
  }

  // Error State (Critical Error)
  if (error && myRequests.length === 0 && !successMessage) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Time Off Requests"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-error)',
              marginBottom: '1rem'
            }}
          ></i>
          <h3 style={{ marginBottom: '1rem', color: 'var(--psyber-error)' }}>Error</h3>
          <p style={{ color: 'var(--psyber-text-secondary)', marginBottom: '2rem' }}>{error}</p>
          <Button variant="primary" icon="fas fa-redo" onClick={loadUserAndRequests}>
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  // Empty State
  const EmptyState = () => (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <i
        className="fas fa-calendar-xmark"
        style={{
          fontSize: '4rem',
          color: 'var(--psyber-text-muted)',
          marginBottom: '1.5rem',
          display: 'block'
        }}
      ></i>
      <h3 style={{ marginBottom: '0.5rem', color: 'var(--psyber-text)' }}>
        No Time Off Requests Yet
      </h3>
      <p style={{ color: 'var(--psyber-text-secondary)' }}>
        Click "Request Time Off" to submit your first request
      </p>
    </div>
  )

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        showBackButton={true}
        onBack={handleBack}
        title="Time Off Requests"
      />

      {/* Success Message */}
      {successMessage && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-success-bg)',
            border: '2px solid var(--psyber-success)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-check-circle" style={{ color: 'var(--psyber-success)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-success)', fontWeight: 600 }}>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && (myRequests.length > 0 || isAdmin) && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</p>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--psyber-font-size-2xl)',
                color: 'var(--psyber-text)'
              }}
            >
              Request Time Off
            </h2>
            <p
              style={{
                margin: '0.5rem 0 0 0',
                fontSize: 'var(--psyber-font-size-sm)',
                color: 'var(--psyber-text-secondary)'
              }}
            >
              {isAdmin
                ? 'Submit a new request for yourself or on behalf of your team.'
                : 'Send a time off request to your manager for approval.'}
            </p>
          </div>

          <Button
            variant="primary"
            icon="fas fa-calendar-plus"
            onClick={handleOpenRequestModal}
            disabled={isSubmitting || !userData}
          >
            Request Time Off
          </Button>
        </div>
      </Card>

      {/* ADMIN VIEW */}
      {isAdmin && (
        <>
          {/* Pending Requests Section */}
          <Card style={{ marginBottom: '2rem' }}>
            <h2 style={{
              margin: '0 0 1rem 0',
              fontSize: 'var(--psyber-font-size-2xl)',
              color: 'var(--psyber-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-clock"></i>
              Pending Requests
              {pendingRequests.length > 0 && (
                <span style={{
                  fontSize: 'var(--psyber-font-size-sm)',
                  background: 'var(--psyber-warning)',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: 'var(--psyber-radius-full)',
                  fontWeight: 600
                }}>
                  {pendingRequests.length}
                </span>
              )}
            </h2>

            {pendingRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', color: 'var(--psyber-text-muted)', marginBottom: '1rem', display: 'block' }}></i>
                <p style={{ color: 'var(--psyber-text-secondary)' }}>No pending requests to review</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '1rem'
              }}>
                {pendingRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    isAdmin={true}
                    isOwnRequest={false}
                    onApprove={handleApprove}
                    onReject={handleOpenRejectModal}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* All Requests Section with Filters */}
          <Card>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: 'var(--psyber-font-size-2xl)',
                color: 'var(--psyber-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <i className="fas fa-list"></i>
                All Requests
              </h2>

              {/* Filters */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Status Filter */}
                <div>
                  <label
                    htmlFor="statusFilter"
                    style={{
                      display: 'block',
                      marginBottom: '0.25rem',
                      fontSize: 'var(--psyber-font-size-xs)',
                      color: 'var(--psyber-text-secondary)',
                      fontWeight: 600
                    }}
                  >
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="psyber-input"
                    style={{ cursor: 'pointer', minWidth: '150px' }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Employee Filter */}
                <div>
                  <label
                    htmlFor="employeeFilter"
                    style={{
                      display: 'block',
                      marginBottom: '0.25rem',
                      fontSize: 'var(--psyber-font-size-xs)',
                      color: 'var(--psyber-text-secondary)',
                      fontWeight: 600
                    }}
                  >
                    Employee
                  </label>
                  <select
                    id="employeeFilter"
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                    className="psyber-input"
                    style={{ cursor: 'pointer', minWidth: '200px' }}
                  >
                    <option value="all">All Employees</option>
                    {employees.map(emp => (
                      <option key={emp.employeeId} value={emp.employeeId}>
                        {emp.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {filteredRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <i className="fas fa-filter" style={{ fontSize: '3rem', color: 'var(--psyber-text-muted)', marginBottom: '1rem', display: 'block' }}></i>
                <p style={{ color: 'var(--psyber-text-secondary)' }}>No requests match the current filters</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '1rem'
              }}>
                {filteredRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    isAdmin={true}
                    isOwnRequest={request.employeeId === userData.employeeId}
                    onApprove={handleApprove}
                    onReject={handleOpenRejectModal}
                    onCancel={handleCancel}
                  />
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* EMPLOYEE VIEW */}
      {!isAdmin && (
        <>
          {/* Request Button */}
          <Card style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: 'var(--psyber-font-size-2xl)',
                  color: 'var(--psyber-text)'
                }}>
                  My Time Off Requests
                </h2>
                <p style={{
                  margin: '0.5rem 0 0 0',
                  fontSize: 'var(--psyber-font-size-sm)',
                  color: 'var(--psyber-text-secondary)'
                }}>
                  {myRequests.length === 0
                    ? 'No requests yet'
                    : `${myRequests.length} request${myRequests.length !== 1 ? 's' : ''} submitted`}
                </p>
              </div>

            </div>
          </Card>

          {/* My Requests List */}
          <Card>
            {myRequests.length === 0 ? (
              <EmptyState />
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '1rem'
              }}>
                {myRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    isAdmin={false}
                    isOwnRequest={true}
                    onCancel={handleCancel}
                  />
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Request Modal */}
      <RequestModal
        isOpen={isRequestModalOpen}
        onClose={handleCloseRequestModal}
        onSubmit={handleSubmitRequest}
        userData={userData}
        isLoading={isSubmitting}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={handleCloseRejectModal}
        onSubmit={handleSubmitReject}
        request={rejectingRequest}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default TimeOff
